import { useState, useEffect, useRef } from 'react';
import { X, UserPlus, Loader2, MapPin, Calendar, Camera, Video, Bell, BellOff } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import axios from 'axios';
import { toast } from 'sonner';
import { loadGoogleMapsScript } from '@/utils/googleMapsLoader';

const AdminAddLeadModal = ({ isOpen, onClose, onAdded }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    date_required: '',
    location: '',
    location_lat: null,
    location_lon: null,
    service_required: [],
    brief: '',
    budget: '',
    max_reveals: 6,
    status: 'pending',
    send_notifications: true
  });
  const [creating, setCreating] = useState(false);

  const locationInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Initialize Google Maps autocomplete when modal opens
  useEffect(() => {
    if (isOpen) {
      loadGoogleMapsScript(() => {
        const attemptInit = (attempts = 0) => {
          if (attempts > 20) return;

          const input = locationInputRef.current;
          if (input && window.google?.maps?.places) {
            initAutocomplete();
          } else {
            setTimeout(() => attemptInit(attempts + 1), 100);
          }
        };
        setTimeout(() => attemptInit(), 200);
      });
    }

    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isOpen]);

  const initAutocomplete = () => {
    const input = locationInputRef.current;
    if (!input || !window.google?.maps?.places) return;

    if (autocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
    }

    autocompleteRef.current = new window.google.maps.places.Autocomplete(input, {
      componentRestrictions: { country: 'au' },
      fields: ['formatted_address', 'geometry']
    });

    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        setFormData(prev => ({
          ...prev,
          location: place.formatted_address,
          location_lat: place.geometry.location.lat(),
          location_lon: place.geometry.location.lng()
        }));
      }
    });
  };

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      service_required: prev.service_required.includes(service)
        ? prev.service_required.filter(s => s !== service)
        : [...prev.service_required, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.service_required.length === 0) {
      toast.error('Please select at least one service');
      return;
    }

    if (!formData.location_lat || !formData.location_lon) {
      toast.error('Please select a location from the dropdown');
      return;
    }

    try {
      setCreating(true);
      const adminEmail = localStorage.getItem('shootzu_admin_email') || 'admin@shootzu.com';

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads`,
        {
          ...formData,
          admin_email: adminEmail
        }
      );

      if (response.data.success) {
        toast.success(`Lead ${response.data.lead_number} created successfully`);
        onAdded();
        onClose();

        // Reset form
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone_number: '',
          date_required: '',
          location: '',
          location_lat: null,
          location_lon: null,
          service_required: [],
          brief: '',
          budget: '',
          max_reveals: 6,
          status: 'pending',
          send_notifications: true
        });
      }
    } catch (error) {
      console.error('Failed to create lead:', error);
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setCreating(false);
    }
  };

  // Get today's date for min attribute
  const today = new Date().toISOString().split('T')[0];

  const sectionNum = (n) => (
    <span className="w-6 h-6 rounded-full bg-[#EDE4F4] flex items-center justify-center text-xs font-bold text-[#78287C] shrink-0" style={{ fontFamily: 'Poppins,sans-serif' }}>
      {n}
    </span>
  );

  return (
    <div className="sz-modal-backdrop overflow-y-auto" onClick={onClose}>
      <div
        className="sz-modal-card w-full max-w-3xl szscroll overflow-y-auto my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — green gradient */}
        <div
          className="p-6 rounded-t-[19px] relative shrink-0"
          style={{ background: 'linear-gradient(120deg, #15803D, #059669)' }}
        >
          <div className="absolute inset-0 bg-black/15 rounded-t-[19px]" />
          <div className="relative flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white m-0" style={{ fontFamily: 'Poppins,sans-serif' }}>
                Add New Lead
              </h2>
              <p className="text-white/80 text-sm mt-1">Manually create a lead on behalf of a user</p>
            </div>
            <button
              onClick={onClose}
              className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto">

          {/* Contact Info */}
          <div>
            <h3 className="sz-h3 text-[#2B3142] mb-3 flex items-center gap-2">
              {sectionNum(1)}
              Contact Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-1.5">First Name *</label>
                <input
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  className="sz-input"
                  required
                />
              </div>
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-1.5">Last Name *</label>
                <input
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  className="sz-input"
                  required
                />
              </div>
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-1.5">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="sz-input"
                  required
                />
              </div>
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-1.5">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => handleChange('phone_number', e.target.value)}
                  placeholder="04XX XXX XXX"
                  className="sz-input"
                  required
                />
              </div>
            </div>
          </div>

          {/* Event Details */}
          <div>
            <h3 className="sz-h3 text-[#2B3142] mb-3 flex items-center gap-2">
              {sectionNum(2)}
              Event Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block sz-overline text-[#8A90A2] mb-1.5">Location *</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8AEBC] z-10" />
                  <input
                    ref={locationInputRef}
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="Start typing an address..."
                    className="sz-input pl-9"
                    required
                  />
                </div>
                {formData.location && !formData.location_lat && (
                  <p className="text-xs text-[#B45309] mt-1">Please select a location from the dropdown</p>
                )}
              </div>
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-1.5">Date Required *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8AEBC] z-10" />
                  <input
                    type="date"
                    value={formData.date_required}
                    onChange={(e) => handleChange('date_required', e.target.value)}
                    min={today}
                    className="sz-input pl-9"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="sz-h3 text-[#2B3142] mb-3 flex items-center gap-2">
              {sectionNum(3)}
              Services Required *
            </h3>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => toggleService('photography')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-[11px] text-sm font-medium transition-all ${
                  formData.service_required.includes('photography')
                    ? 'bg-[#572B7C] text-white shadow-lg'
                    : 'bg-[#EEF0F4] text-[#8A90A2] hover:bg-[#E0E3EB]'
                }`}
              >
                <Camera className="w-5 h-5" />
                Photography
              </button>
              <button
                type="button"
                onClick={() => toggleService('videography')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-[11px] text-sm font-medium transition-all ${
                  formData.service_required.includes('videography')
                    ? 'bg-[#572B7C] text-white shadow-lg'
                    : 'bg-[#EEF0F4] text-[#8A90A2] hover:bg-[#E0E3EB]'
                }`}
              >
                <Video className="w-5 h-5" />
                Videography
              </button>
            </div>
          </div>

          {/* Additional */}
          <div>
            <h3 className="sz-h3 text-[#2B3142] mb-3 flex items-center gap-2">
              {sectionNum(4)}
              Additional Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-1.5">Budget (optional)</label>
                <input
                  value={formData.budget}
                  onChange={(e) => handleChange('budget', e.target.value)}
                  placeholder="e.g. $500-$1000"
                  className="sz-input"
                />
              </div>
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-1.5">Max Reveals</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={formData.max_reveals}
                  onChange={(e) => handleChange('max_reveals', parseInt(e.target.value) || 6)}
                  className="sz-input"
                />
              </div>
            </div>
            <div>
              <label className="block sz-overline text-[#8A90A2] mb-1.5">Project Brief *</label>
              <textarea
                value={formData.brief}
                onChange={(e) => handleChange('brief', e.target.value)}
                rows={4}
                placeholder="Describe the project requirements, event type, etc..."
                className="sz-input resize-none"
                required
              />
            </div>
          </div>

          {/* Status Options */}
          <div>
            <h3 className="sz-h3 text-[#2B3142] mb-3 flex items-center gap-2">
              {sectionNum(5)}
              Status Options
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-1.5">Initial Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="sz-input"
                >
                  <option value="pending">Pending (needs approval)</option>
                  <option value="approved">Approved (live immediately)</option>
                </select>
              </div>
              {formData.status === 'approved' && (
                <div className="flex items-center gap-3 sm:pt-7">
                  <Checkbox
                    checked={formData.send_notifications}
                    onCheckedChange={(checked) => handleChange('send_notifications', checked)}
                    id="notify_users"
                  />
                  <Label htmlFor="notify_users" className="cursor-pointer flex items-center gap-2 text-[#2B3142]">
                    {formData.send_notifications ? (
                      <Bell className="w-4 h-4 text-[#15803D]" />
                    ) : (
                      <BellOff className="w-4 h-4 text-[#A8AEBC]" />
                    )}
                    Notify nearby users
                  </Label>
                </div>
              )}
            </div>
            {formData.status === 'approved' && (
              <p className="text-xs text-[#8A90A2] mt-2">
                {formData.send_notifications
                  ? 'Nearby photographers will be notified via email and push notification'
                  : 'Lead will be approved silently (no notifications sent)'}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-[#F1F3F7]">
            <button
              type="submit"
              disabled={creating}
              className="sz-btn flex-1 h-12 text-white font-semibold disabled:opacity-60"
              style={{ background: 'linear-gradient(120deg, #15803D, #059669)' }}
            >
              {creating ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
              ) : (
                <><UserPlus className="w-4 h-4" /> Create Lead</>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="sz-btn sz-btn-outline h-12 px-8"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAddLeadModal;
